import { apiClient } from './client';
import {
  SpotRequest,
  IncomingRequest,
  SpotOffer,
  SpotRequestDetail,
  SpotRequestSchedule,
} from '../store/spotRequestStore';

interface PaginationMeta {
  total: number;
  page: number;
  per_page: number;
}

interface ListResponse<T> {
  data: T[];
  meta: PaginationMeta;
}

export async function createSpotRequest(
  requestedFrom: string,
  requestedUntil: string,
  note?: string,
): Promise<SpotRequest> {
  const payload: any = {
    requested_from: requestedFrom,
    requested_until: requestedUntil,
  };
  if (note) {
    payload.note = note;
  }
  console.log('[createSpotRequest] payload:', payload);
  const { data } = await apiClient.post('/spot-requests', payload);
  return data.data;
}

export async function getMyRequests(
  page: number = 1,
): Promise<ListResponse<SpotRequest>> {
  const { data } = await apiClient.get('/spot-requests', {
    params: { page, per_page: 20 },
  });
  return data;
}

export async function getIncomingRequests(
  page: number = 1,
): Promise<ListResponse<IncomingRequest>> {
  const { data } = await apiClient.get('/spot-requests/incoming', {
    params: { page, per_page: 20 },
  });
  return data;
}

export async function getRequestDetail(requestId: string): Promise<SpotRequestDetail> {
  const { data } = await apiClient.get(`/spot-requests/${requestId}`);
  return data.data;
}

export async function cancelSpotRequest(requestId: string): Promise<void> {
  await apiClient.patch(`/spot-requests/${requestId}`, { action: 'cancel' });
}

export async function editSpotRequest(
  requestId: string,
  requestedFrom: string,
  requestedUntil: string,
): Promise<SpotRequest> {
  const { data } = await apiClient.patch(`/spot-requests/${requestId}`, {
    action: 'edit',
    requested_from: requestedFrom,
    requested_until: requestedUntil,
  });
  return data.data;
}

export async function createSpotOffer(
  requestId: string,
  spotId: string,
  offeredFrom: string,
  offeredUntil: string,
  message?: string,
): Promise<SpotOffer> {
  const { data } = await apiClient.post('/spot-offers', {
    request_id: requestId,
    spot_id: spotId,
    offered_from: offeredFrom,
    offered_until: offeredUntil,
    message,
  });
  return data.data;
}

export async function acceptSpotOffer(
  offerId: string,
): Promise<{ sessionId: string; spotId: string }> {
  const { data } = await apiClient.patch(`/spot-offers/${offerId}/accept`);
  return data.data;
}

export async function rejectSpotOffer(offerId: string): Promise<void> {
  await apiClient.patch(`/spot-offers/${offerId}/reject`);
}

// ─── recurring request schedules ─────────────────────────────────────────────

export async function getRequestSchedules(): Promise<SpotRequestSchedule[]> {
  const { data } = await apiClient.get('/spot-requests/schedules');
  return data.data;
}

export async function createRequestSchedule(
  daysOfWeek: number[],
  startTime: string,
  endTime: string,
): Promise<SpotRequestSchedule> {
  const { data } = await apiClient.post('/spot-requests/schedules', {
    days_of_week: daysOfWeek,
    start_time: startTime,
    end_time: endTime,
  });
  return data.data;
}

export async function setRequestScheduleActive(
  scheduleId: string,
  isActive: boolean,
): Promise<SpotRequestSchedule> {
  const { data } = await apiClient.patch(`/spot-requests/schedules/${scheduleId}`, {
    is_active: isActive,
  });
  return data.data;
}

export async function deleteRequestSchedule(scheduleId: string): Promise<void> {
  await apiClient.delete(`/spot-requests/schedules/${scheduleId}`);
}
